<template>
  <div>
    <Header>User Management</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="mx-auto my-4 pb-4">
        <div class="flex flex-col justify-center items-center mt-4 gap-2">
          <div class="grid grid-cols-3 bg-gray-50 rounded-md shadow-sm p-2 w-full">
            <div class="flex flex-row justify-between items-center">
              <span>
                API Token Status: {{ tokenStatus }}
              </span>
              <Spinner class="pr-2"
                v-if="refreshingTokenSpinner"
                message="Refreshing"
                :size="4"
              />
            </div>
            <Button class="w-96"
              color="gray"
              size="md"
              :disabled="performingApiAction"
              @click="checkAuth0ManagementApiToken">
              Check
            </Button>
            <Button class="w-96"
              color="gray"
              size="md"
              :disabled="performingApiAction"
              @click="refreshAuth0ManagementApiToken">
              Refresh
            </Button>
          </div>
        </div>
      </div>
      <div class="mx-auto my-4">
        <ManagementBatchTextEditor
          v-model="userManagement.query"
          :disabled="userManagement.disabled"
          :resizable="!userManagement.disabled"
          :errorsMap="errors"
        />
      </div>
      <div class="mx-auto my-4">
        <NewUser
          :userGroupOptions="userGroupOptions"
          :userConnectionOptions="userConnectionOptions"
          @addQuery="addQueryToBatch"
        />
      </div>
      <div class="mx-auto my-4">
        <div class="flex flex-row">
          <div class="p-2">
            <Button class="w-96"
              color="gray"
              size="md"
              @click="clearUserChanges">
              Clear
            </Button>
          </div>
          <div class="grow"></div>
          <div class="p-2 rounded-md bg-sky-200">
            <Button class="w-96"
              color="blue"
              size="md"
              :disabled="performingApiAction"
              @click="commitUserChanges">
              Confirm Changes
            </Button>
          </div>
        </div>
      </div>
      <div class="mx-auto my-4">
        <div class="flex flex-col justify-center items-center mt-4 gap-2">
          <div class="grid grid-cols-3 bg-gray-50 rounded-md shadow-sm p-2 w-full">
            <div class="flex flex-row justify-between items-center">
              <span>
                API Token Status: {{ tokenStatus }}
              </span>
              <Spinner class="pr-2"
                v-if="refreshingTokenSpinner"
                message="Refreshing"
                :size="4"
              />
            </div>
            <Button class="w-96"
              color="gray"
              size="md"
              :disabled="performingApiAction"
              @click="checkAuth0ManagementApiToken">
              Check
            </Button>
            <Button class="w-96"
              color="gray"
              size="md"
              :disabled="performingApiAction"
              @click="refreshAuth0ManagementApiToken">
              Refresh
            </Button>
          </div>
          <div class="grid grid-cols-3 bg-gray-50 rounded-md shadow-sm p-2 w-full gap-2">
            <div class="flex flex-row col-span-1 justify-between items-center">
              <span>
                Users
              </span>
            </div>
            <TextField class="flex flex-row col-span-2 justify-between items-center px-2"
              :disabled="loadingUsersSpinner"
              v-model="userQuery.search">
              Search:
            </TextField>
            <div class="flex col-span-3 justify-end">
              <Spinner class="pr-2"
                v-if="loadingUsersSpinner"
                message="Loading"
                :size="4"
              />
              <Button class="w-96"
                color="gray"
                size="md"
                :disabled="performingApiAction"
                @click="refreshAuth0Users">
                Get
              </Button>
            </div>
          </div>
        </div>
        <div class="flex relative justify-center items-center mt-4">
          <Pagination :pages="pages" to="/UserManagementSync" />
          <div class="absolute absolute-right right-0 text-gray-500 text-sm m-2">
            Total Users: {{ totalUsers }}
          </div>
        </div>
        <Spinner
          v-if="loadingUsersSpinner"
          message="Loading Users"
          :size="16"
        />
        <div class="bg-gray-50 rounded-md shadow-sm p-2">
          <div class="bg-gray-100 rounded-md space-y-2 pb-6">
            <div class="grid grid-cols-12">
              <div class="p-2 border-gray-300 border-solid border-b-2"
                v-for="(header, index) in headers"
                :key="index"
                :class="{
                  'col-span-2': [0, 1, 2, 3].includes(index % 6),
                  'col-span-3': index % 6 === 4,
                  'col-span-1': index % 6 === 5,
                }">
                {{ header }}
              </div>
            </div>
            <ExistingUser class="w-full"
              v-for="(item, index) in displayUsers"
              :key="index"
              :auth0User="item"
              :listIndex="index"
              :portalUser="portalUsers[item.user_id]"
              :userGroupOptions="userGroupOptions"
              :showPortalUserError="!loadingUsersSpinner"
              @addUserQuery="addUserQuery"
            />
          </div>
        </div>
        <div class="flex justify-center pb-4" v-if="displayUsers.length > 0">
          <Pagination :pages="pages" to="/UserManagementSync" />
        </div>
      </div>
    </Main>
    <Modal ref="confirmChangesModal" size="sm" anchor="center" :onSuccess="handleSubmit">
      <template #title>Confirm Changes</template>
      <template #content>
        <div>
          Number of Queries: {{ userManagement.numQueries }}
        </div>
      </template>
      <template #footer="slotProps">
        <SubmitButton
          type="button"
          class="rounded-md font-medium text-white bg-sky-600 px-3 py-2 hover:bg-blue-200 transition-colors shadow-sm"
          :submitting="batchManageUsersSubmitting"
          @click="
            () => {
              slotProps.closeModal(true)
            }
          ">
          Commit Changes
        </SubmitButton>
      </template>
    </Modal>
    <ItemSelectorModals />
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'
import Modal from '../components/Modal.vue'
import Pagination from '../components/Pagination.vue'
import Spinner from '../components/Spinner.vue'

import TextField from '../components/formComponents/TextField.vue'
import Button from '../components/Button.vue'
import SubmitButton from '../components/SubmitButton.vue'

import NewUser from '../components/UserManagement/NewUser.vue'
import ExistingUser from '../components/UserManagement/ExistingUser.vue'
import ManagementBatchTextEditor from '../components/UserManagement/ManagementBatchTextEditor.vue'
import ItemSelectorModals from '../components/UserManagement/ItemSelector/ItemSelectorModals.vue'

import userManagementService from '../services/pages/userManagement.js'

import notifications from '../services/notifications.js'
import '../services/validation.js'
import user from '../services/user/user.js'

export default {
  props: {},
  components: {
    Main,
    Header,
    Modal,
    Pagination,
    Spinner,
    TextField,
    Button,
    SubmitButton,
    NewUser,
    ExistingUser,
    ManagementBatchTextEditor,
    ItemSelectorModals,
  },
  data() {
    return {
      user: undefined,
      canViewAuth0Users: false,
      canChangeAuth0Users: false,

      userGroupOptions: [],
      userConnectionOptions: [
        'Username-Password-Authentication',
        'Kanan-Wordpress-Users',
      ],

      userManagement: {
        query: "",
        disabled: false,
        numQueries: undefined,
        finalQueries: undefined,
      },

      userQuery: {
        perPage: undefined,
        page: undefined,
        search: undefined,
      },

      tokenStatus: undefined,

      pages: {},
      displayUsers: [],
      totalUsers: 0,

      portalUsers: [],

      errors: undefined,

      batchManageUsersSubmitting: false,
      refreshingTokenSpinner: false,
      loadingUsersSpinner: false,

      headers: [
        'Name',
        'Email',
        'User ID',
        'Groups',
        'Config',
        'Actions',
      ],
    }
  },
  methods: {
    async updateUser() {
      this.user = await user.get()
      this.validateProvidedInfo()
    },
    async updateUserGroups() {
      await user.ready()
      this.userGroupOptions = await userManagementService.getUserGroups()
    },
    validateProvidedInfo() {
      this.canViewAuth0Users = userManagementService.canViewAuth0Users(this.user)
      this.canChangeAuth0Users = userManagementService.canChangeAuth0Users(this.user)
    },

    checkAuth0ManagementApiToken() {
      userManagementService.statusManagementApiToken()
        .then((status) => {
          this.tokenStatus = status
        })
    },
    refreshAuth0ManagementApiToken() {
      if (this.tokenStatus === undefined) {
        notifications.pushInfo('Check Token status first')
        return
      }
      else if (this.tokenStatus === 'Valid') {
        notifications.pushInfo('Token status already valid')
        return
      }
      this.refreshingTokenSpinner = true
      userManagementService.refreshManagementApiToken()
        .then(() => {
          this.refreshingTokenSpinner = false
          this.tokenStatus = 'Valid'
        })
    },

    async refreshAuth0Users() {
      this.loadingUsersSpinner = true

      let displayData
      try {

        let apiParams = {
          page: this.$route.query.page,
          perPage: this.userQuery.perPage,
          search: this.userQuery.search,
        }
        displayData = await userManagementService.getUsers(apiParams)

        this.pages = displayData.pages
        this.displayUsers = displayData.content
        this.totalUsers = displayData.page_data.num_users
      }
      catch (error) {
        this.loadingUsersSpinner = false
        console.log(error)
        return
      }

      try {
        let auth0Ids = this.displayUsers.map((item) => item.user_id)
        console.log(auth0Ids)
        let portalUserData = await userManagementService.getPortalUsers(auth0Ids)

        this.portalUsers = userManagementService.matchPortalUsers(portalUserData)

        console.log(this.portalUsers)
      }
      catch (error) {
        this.loadingUsersSpinner = false
        console.log(error)
        return
      }

      this.loadingUsersSpinner = false
    },

    addQueryToBatch(query) {
      if (this.userManagement.query) {
        this.userManagement.query += '\n'
      }
      this.userManagement.query += query
    },

    clearUserChanges() {
      this.userManagement.query = ''
    },
    commitUserChanges() {
      this.userManagement.finalQueries = this.userManagement.query.split('\n').filter(line => line.trim().length > 0)
      this.userManagement.numQueries = this.userManagement.finalQueries.length

      console.log(JSON.stringify(this.userManagement.finalQueries))
      this.$refs['confirmChangesModal'].openModal()
    },

    markPendingUser(auth0User) {
      userManagementService.markPendingUser(auth0User)
    },

    addUserQuery(newQuery) {
      console.log(newQuery)
      this.addQueryToBatch(newQuery)
    },

    async handleSubmit() {
      try {
        this.batchManageUsersSubmitting = true
        let errors = await userManagementService.batchManageUsersCommands(this.userManagement.finalQueries)
        this.batchManageUsersSubmitting = false

        if (errors !== undefined) {
          this.errors = errors
          return
        }

        this.userManagement.finalQueries = undefined
        this.userManagement.numQueries = undefined
        this.userManagement.query = ""

        this.refreshAuth0Users()
      }
      catch (error) {
        this.batchManageUsersSubmitting = false
        console.log(error)
      }
    },
  },
  async mounted() {
    this.userQuery = {
      ...this.$route.query
    }
    await this.updateUser()
    await this.updateUserGroups()
    // Don't do this automatically on this page.
    // this.refreshAuth0Users()
  },
  computed: {
    performingApiAction() {
      return this.batchManageUsersSubmitting ||
        this.refreshingTokenSpinner ||
        this.loadingUsersSpinner
    }
  },
  watch: {
    '$route.query.page': function () {
      this.refreshAuth0Users()
    },
    '$route.query': function (newValue) {
      this.userQuery = {
        ...newValue
      }
      // this.refreshAuth0Users()
    },
    userQuery: {
      handler: function (newValue) {
        if (this.$route.query.page === newValue.page &&
            this.$route.query.perPage === newValue.perPage &&
            this.$route.query.search === newValue.search) {
          return
        }

        this.$router.push({
          query: {
            ...newValue,
          }
        })
      },
      deep: true
    },
  },
}
</script>
